import { Resource } from "permissions/resources/permission-resource";
import {
  ResourceType,
  AccessLevel,
  Scope,
  AnyResourceJSON,
  ResourceFromJSON
} from "permissions/resources/permission-resource-types";
import LoginAccessSchema, {
  LoginAccessParams,
  LoginAccessJSON
} from "permissions/resources/users/_schemas/login-access-schema";
import { LoginAccessType } from "permissions/resources/users/login-access-type";
import UserNetworkingAccessSchema, { 
  UserNetworkingAccessParams, 
  UserNetworkingAccessJSON 
} from "permissions/resources/users/_schemas/user-networking-access-schema";
import { registerResource } from "permissions/resources/resource-registry";

export class LoginAccess extends Resource {
  public static create(params?: LoginAccessParams): LoginAccess {
    const parsed = LoginAccessSchema.validate(params);
    return new LoginAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const inputJson = json as LoginAccessJSON;
    const parsed = LoginAccessSchema.deserialize(inputJson);
    return new LoginAccess(parsed) as ResourceFromJSON<J>;
  }

  public static getPermutations(): LoginAccess[] {
    const fieldIterations: [key: string, value: number[]][] = [
      ['accessType', Object.values(LoginAccessType).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
    ];
    return this.generatePermutations(LoginAccess, fieldIterations) as LoginAccess[];
  }

  public readonly resourceType = ResourceType.LoginAccess;
  public readonly accessType?: LoginAccessType

  public constructor(params?: LoginAccessParams) {
    super(params);

    if (params) {
      this.accessType = params.accessType;
    }
  }

  public toJSON(): LoginAccessJSON {
    return LoginAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `LoginAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? 'true' : 'false'}
      resourceTypeText: ${this.resourceTypeText}
      accessType: ${this.accessType}
    }`
  }
}

registerResource("LoginAccess", (json) => LoginAccess.fromJSON(json));

export class UserNetworkingAccess extends Resource {
  public static create(params?: UserNetworkingAccessParams): UserNetworkingAccess {
    const parsed = UserNetworkingAccessSchema.validate(params);
    return new UserNetworkingAccess(parsed);
  }

  public static fromJSON<J extends AnyResourceJSON>(json: J): ResourceFromJSON<J> {
    const parsed = UserNetworkingAccessSchema.deserialize(json as UserNetworkingAccessJSON);
    return new UserNetworkingAccess(parsed) as ResourceFromJSON<J>;
  }

  public static getPermutations(): UserNetworkingAccess[] {
    const fieldIterations: [key: string, value: number[]][] = [
      ['accessLevel', Object.values(AccessLevel).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
      ['scope', Object.values(Scope).filter((key) => !Number.isNaN(Number(key))).map(value => Number(value))],
    ];
    return this.generatePermutations(UserNetworkingAccess, fieldIterations) as UserNetworkingAccess[];
  }

  public readonly resourceType = ResourceType.UserNetworkingAccess;
  public readonly accessLevel?: AccessLevel;
  public readonly scope?: Scope;

  public constructor(params?: UserNetworkingAccessParams) {
    super(params);

    if (params) {
      this.accessLevel = params.accessLevel;
      this.scope = params.scope;
    }
  }

  public toJSON(): UserNetworkingAccessJSON {
    return UserNetworkingAccessSchema.serialize(this);
  }

  public override toString(): string {
    return `UserNetworkingAccess {
      resourceType: ${this.resourceType}
      isDisabled: ${this.isDisabled ? 'true' : 'false'}
      resourceTypeText: ${this.resourceTypeText}
      accessLevel: ${this.accessLevel}
      scope: ${this.scope}
    }`
  }
}

registerResource("UserNetworkingAccess", (json) => UserNetworkingAccess.fromJSON(json));
